/* eslint-disable no-await-in-loop */
/* eslint-disable react/no-array-index-key */
import {
    BarChart,
    Button,
    Divider,
    Flex,
    Grid,
    ProgressBar,
    Tab,
    TabGroup,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TabList,
    Text,
    Textarea,
    Title,
} from '@tremor/react'
import { DragEvent, DragEventHandler, useRef, useState } from 'react'
import WatadLogo from '../../images/watad-large.png'
import { useAuth } from '../../utilities/auth'
import Spinner from '../../components/Spinner'

const lang = [
    {
        lang: 'en',
        title: 'Identification and quantization of microalgae',
        upload: 'Drag and drop or select a file to upload and analyze the image.',
        submit: 'Analyze and count',
        changeLangAr: 'Arabic',
        changeLangEn: 'English',
        originalImage: 'Original image',
        results: 'Results',
        count: 'Count',
        show: 'View full report',
        processingTitle: 'Processing input image',
        processing1: 'Analyzing',
        processing2: 'Identification',
        processing3: 'Quantization',
        processing4: 'Generating Report',
        cancel: 'Cancel',
        close: 'Close',
        tableTitle: 'List of detected microalgaes',
        type: 'Type',
        confidence: 'Confidence',
        width: 'Width (pixels)',
        height: 'Height (pixels)',
        metadata: 'Metadata',
    },
    {
        lang: 'ar',
        title: 'تحديد وتصنيف الطحالب الدقيقة',
        upload: 'اسحب وأفلت أو اختر ملفًا لتحميل الصورة وتحليلها',
        submit: 'تحديد وعدّ',
        changeLangAr: 'العربية',
        changeLangEn: 'الإنجليزية',
        originalImage: 'الصورة الأصلية',
        results: 'النتائج',
        count: 'العدد',
        show: 'عرض التقرير الكامل',
        processingTitle: 'معالجة الصورة المدخلة',
        processing1: 'تحليل',
        processing2: 'تحديد',
        processing3: 'تصنيف',
        processing4: 'إنشاء التقرير',
        cancel: 'يلغي',
        close: 'يغلق',
        tableTitle: 'قائمة الطحالب الدقيقة المكتشفة',
        type: 'يكتب',
        confidence: 'ثقة',
        width: 'العرض (بكسل)',
        height: 'الارتفاع (بكسل)',
        metadata: 'البيانات الوصفية',
    },
]

interface Report {
    original_image: string
    metadata: string

    predictions: {
        boxes: {
            box: number[]
            confidence: number
            width: number
            height: number
            class: number
            className: string
        }[]
        image_width: number
        image_height: number
        annotated_image: string
    }[]
}

export default function MicroAlgaePage() {
    const { user } = useAuth()
    const [reports, setReports] = useState<Report[]>([])
    const [selectedReport, setSelectedReport] = useState<Report | undefined>()
    const [analyzeStep, setAnalyzeStep] = useState<number>(0)
    const [error, setError] = useState<string>('')
    const [language, setLanguage] = useState('ar')
    const [metadata, setMetadata] = useState('')
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const l = lang.filter((v) => v.lang === language).at(0)

    const uploadClick = () => {
        fileInputRef.current?.click()
    }
    const uploadDragOver = (event: DragEvent<HTMLDivElement>) => {
        uploadClick()
        event.preventDefault()
    }
    const uploadDrop = (event: DragEvent<HTMLDivElement>) => {
        uploadClick()
    }

    // Function to handle file change event and read the file content
    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setAnalyzeStep(1)
        try {
            if (event.target.files !== null) {
                for (let i = 0; i < event.target.files.length; i += 1) {
                    const file = event.target.files.item(i)
                    if (file) {
                        const formData = new FormData()
                        setError('')
                        formData.append('file', event.target.files[0])

                        // 2. Send request to your FastAPI endpoint
                        const response = await fetch(
                            'http://localhost:8000/predict',
                            {
                                method: 'POST',
                                body: formData,
                            }
                        )
                        if (!response.ok) {
                            throw new Error(
                                `Server error: ${response.statusText}`
                            )
                        }
                        setAnalyzeStep(3)

                        const data = await response.json()
                        const report = data as Report
                        const img = await file.arrayBuffer()
                        const blob = new Blob([img], { type: 'image/png' })
                        report.original_image = URL.createObjectURL(blob)
                        report.metadata = metadata
                        setMetadata('')

                        setReports([report, ...reports])
                        setSelectedReport(report)
                    }
                }
            }
        } catch (e) {
            setError(String(e))
        }
        setAnalyzeStep(0)
    }

    const count = (r: Report) => {
        const classes = r.predictions.flatMap((p) =>
            p.boxes.map((b) => b.className)
        )
        const classMap = new Map<string, number>()
        classes.forEach((cls) => {
            // Get current count or default to 0
            const currentCount = classMap.get(cls) ?? 0
            classMap.set(cls, currentCount + 1)
        })
        return Array.from(classMap.entries())
    }
    const analyzeProgress = () => {
        switch (analyzeStep) {
            case 1:
                return 32
            case 2:
                return 64
            case 3:
                return 100
            default:
                return 0
        }
    }
    const selectedCount =
        selectedReport === undefined ? undefined : count(selectedReport)

    return (
        <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <Flex
                className="p-4 m-5 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
                flexDirection="col"
                justifyContent="start"
                alignItems="start"
            >
                <Grid numItems={3} className="w-full">
                    <Flex className="w-40 mt-4">
                        <WatadLogo />
                    </Flex>
                    <Title className="w-full text-center mt-5">
                        {l?.title}
                    </Title>
                    <Flex
                        flexDirection="row"
                        justifyContent="end"
                        className="w-full "
                    >
                        <Flex
                            flexDirection="row"
                            className="w-fit border py-1 pl-4 pr-1 rtl:pl-1 rtl:pr-4 rounded-full"
                        >
                            <div>{user.email}</div>
                            <div className="ml-2 rtl:mr-2 rtl:ml-0  w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold bg-teal-500">
                                {user.name?.at(0)?.toUpperCase()}
                            </div>
                        </Flex>
                    </Flex>
                </Grid>
                <TabGroup
                    className="mt-4"
                    onIndexChange={(i) => {
                        console.log(i)
                        setLanguage(i === 0 ? 'ar' : 'en')
                    }}
                >
                    <TabList variant="solid" color="teal">
                        <Tab>{l?.changeLangAr}</Tab>
                        <Tab>{l?.changeLangEn}</Tab>
                    </TabList>
                </TabGroup>

                <Flex
                    className="border-dashed cursor-pointer border-2 mt-2 rounded-xl border-gray-300 py-12 px-5"
                    flexDirection="col"
                    onClick={uploadClick}
                    onDragOver={uploadDragOver}
                    onDrop={uploadDrop}
                >
                    <div
                        id="spinner-container"
                        className="spinner-container hidden"
                    >
                        <div className="spinner" />
                    </div>
                    <div id="upload-icon" className="upload-icon block">
                        <svg
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M8.96997 6.53002C8.67697 6.23702 8.67697 5.76199 8.96997 5.46899L11.97 2.46899C12.039 2.39999 12.1221 2.345 12.2141 2.307C12.3971 2.231 12.6041 2.231 12.7871 2.307C12.8791 2.345 12.962 2.39999 13.031 2.46899L16.031 5.46899C16.324 5.76199 16.324 6.23702 16.031 6.53002C15.885 6.67602 15.693 6.74999 15.501 6.74999C15.309 6.74999 15.1169 6.67702 14.9709 6.53002L13.251 4.80999V16C13.251 16.414 12.915 16.75 12.501 16.75C12.087 16.75 11.751 16.414 11.751 16V4.81097L10.031 6.531C9.73701 6.823 9.26297 6.82302 8.96997 6.53002ZM18.5 9.24999C18.086 9.24999 17.75 9.58599 17.75 9.99999C17.75 10.414 18.086 10.75 18.5 10.75C20.077 10.75 20.75 11.423 20.75 13V18C20.75 19.577 20.077 20.25 18.5 20.25H6.5C4.923 20.25 4.25 19.577 4.25 18V13C4.25 11.423 4.923 10.75 6.5 10.75C6.914 10.75 7.25 10.414 7.25 9.99999C7.25 9.58599 6.914 9.24999 6.5 9.24999C4.082 9.24999 2.75 10.582 2.75 13V18C2.75 20.418 4.082 21.75 6.5 21.75H18.5C20.918 21.75 22.25 20.418 22.25 18V13C22.25 10.582 20.918 9.24999 18.5 9.24999Z"
                                fill="#343434"
                            />
                        </svg>
                    </div>
                    <p>{l?.upload}</p>
                    <input
                        key="input_attachment"
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </Flex>
                <Flex
                    flexDirection="col"
                    justifyContent="center"
                    className="mt-2"
                >
                    <Textarea
                        value={metadata}
                        onValueChange={setMetadata}
                        className="my-4"
                        placeholder={l?.metadata}
                    />
                    {error && <p style={{ color: 'red' }}>{error}</p>}

                    <Button
                        variant="secondary"
                        color="teal"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {l?.submit}
                    </Button>
                </Flex>
                <Divider className="my-2" />
                <Flex flexDirection="col" className="space-y-4">
                    <Title>{l?.results}</Title>
                    <Grid numItems={3}>
                        {reports.map((r) =>
                            r.predictions.map((p) => {
                                return (
                                    <Flex
                                        flexDirection="col"
                                        className="border p-1 rounded-xl space-y-3"
                                    >
                                        <img
                                            className="rounded-xl w-96"
                                            src={`data:image/png;base64,${p.annotated_image}`}
                                            alt="results"
                                        />
                                        <Title>
                                            {l?.count}: {p.boxes.length}
                                        </Title>
                                        {count(r).map((item) => (
                                            <Text>
                                                {item[0]}: {item[1]}
                                            </Text>
                                        ))}
                                        <Button
                                            color="teal"
                                            onClick={() => setSelectedReport(r)}
                                        >
                                            {l?.show}
                                        </Button>
                                    </Flex>
                                )
                            })
                        )}
                    </Grid>
                </Flex>
            </Flex>
            {selectedReport !== undefined && (
                <Flex
                    justifyContent="center"
                    className="fixed top-0 left-0 w-full h-full bg-gray-400/80"
                >
                    <Flex
                        flexDirection="col"
                        className="bg-white rounded-md m-10 p-10 w-full h-full overflow-y-auto"
                    >
                        <Flex
                            flexDirection="row"
                            justifyContent="start"
                            alignItems="start"
                        >
                            <Flex
                                flexDirection="col"
                                className="rtl:ml-2 ltr:mr-2"
                            >
                                <Title>
                                    {l?.originalImage} - (w:{' '}
                                    {
                                        selectedReport.predictions.at(0)
                                            ?.image_width
                                    }
                                    , h:{' '}
                                    {
                                        selectedReport.predictions.at(0)
                                            ?.image_height
                                    }
                                    )
                                </Title>
                                <img
                                    className="rounded-xl w-full mb-5"
                                    src={selectedReport.original_image}
                                    alt="results"
                                />
                                <Title>{l?.metadata}:</Title>
                                <Text>{selectedReport.metadata}</Text>
                            </Flex>
                            <Flex flexDirection="col">
                                <Title>{l?.results}</Title>
                                <img
                                    className="rounded-xl w-full mb-5"
                                    src={`data:image/png;base64,${selectedReport.predictions
                                        .map((p) => p.annotated_image)
                                        .at(0)}`}
                                    alt="results"
                                />
                                <Flex flexDirection="col" className="my-2">
                                    <Title className="my-4">
                                        {l?.tableTitle}
                                    </Title>
                                    <Table className="w-full">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>#</TableCell>
                                                <TableCell>{l?.type}</TableCell>
                                                <TableCell>
                                                    {l?.confidence}
                                                </TableCell>
                                                <TableCell>
                                                    {l?.width}
                                                </TableCell>
                                                <TableCell>
                                                    {l?.height}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedReport.predictions
                                                .flatMap((p) => p.boxes)
                                                .map((b, idx) => {
                                                    return (
                                                        <TableRow>
                                                            <TableCell>
                                                                {idx + 1}
                                                            </TableCell>
                                                            <TableCell>
                                                                {b.className}
                                                            </TableCell>
                                                            <TableCell>
                                                                {(
                                                                    b.confidence *
                                                                    100
                                                                ).toFixed(2)}
                                                                %
                                                            </TableCell>
                                                            <TableCell>
                                                                {b.width.toFixed(
                                                                    0
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {b.height.toFixed(
                                                                    0
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                        </TableBody>
                                    </Table>
                                </Flex>
                            </Flex>
                        </Flex>

                        <Flex>
                            <Button
                                variant="secondary"
                                color="teal"
                                onClick={() => setSelectedReport(undefined)}
                            >
                                {l?.close}
                            </Button>
                        </Flex>
                    </Flex>
                </Flex>
            )}
            {analyzeStep > 0 && (
                <Flex
                    justifyContent="center"
                    className="fixed top-0 left-0 w-full h-full bg-gray-400/80"
                >
                    <Flex
                        flexDirection="col"
                        className="bg-white rounded-md m-10 p-10 w-1/2"
                    >
                        <Title className="mb-20 text-teal-600">
                            {l?.processingTitle}
                        </Title>
                        <ProgressBar color="teal" value={analyzeProgress()} />
                        <Flex>
                            <Text className="text-teal-600">
                                {l?.processing1}
                            </Text>
                            <Text className="text-teal-600">
                                {l?.processing2}
                            </Text>
                            <Text className="text-teal-600">
                                {l?.processing3}
                            </Text>
                            <Text className="text-teal-600">
                                {l?.processing4}
                            </Text>
                        </Flex>
                        <Flex
                            alignItems="end"
                            justifyContent="end"
                            className="mt-10"
                        >
                            <Button variant="secondary" color="teal">
                                {l?.cancel}
                            </Button>
                        </Flex>
                    </Flex>
                </Flex>
            )}
        </div>
    )
}
